import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { badgeRecipe } from '../recipes/badge.recipe';
import {
  statusPillChevronRecipe,
  statusPillTriggerRecipe,
} from '../recipes/status-pill-menu.recipe';
import { Badge, type BadgeProps, type JobPhase } from './Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './DropdownMenu';
import { Spinner } from './Spinner';

export interface StatusPillOption {
  /** The code the API takes: DISPATCHED, ON_SCENE. */
  value: string;
  /** The words: "On scene". */
  label: string;
  /** The job phase that colours it, or a severity tone. One or the other. */
  phase?: JobPhase;
  tone?: BadgeProps['tone'];
}

/**
 * A menu to put on a status pill, as a view's caller hands it over: what
 * it may become, what to do when one is picked, and whether a pick is
 * saving. Left out, the pill is a plain badge.
 */
export interface StatusMenu {
  options: StatusPillOption[];
  onChange: (status: string) => void;
  busy?: boolean;
}

export interface StatusPillMenuProps {
  /** The current code. Need not be one of `options`: see `label`. */
  value: string;
  /** What it can be changed to, in the order they happen. */
  options: StatusPillOption[];
  /**
   * Called with the code chosen. Left out, the pill is a plain Badge: the
   * same marker a role that may not change the status has always seen.
   */
  onChange?: (value: string) => void;
  /** Shown but will not open. */
  disabled?: boolean;
  /** A change is saving: a spinner in place of the chevron, and no opening. */
  busy?: boolean;
  /**
   * What the closed pill says. Falls back to the matching option's label,
   * then to the raw value. The map's roster passes its own words here ("No
   * signal", "Available") while `value` stays the code the menu marks.
   */
  label?: string;
  /**
   * The closed pill's colour. Either one set here wins over the matching
   * option's; with neither set the option's colour is used.
   */
  phase?: JobPhase;
  tone?: BadgeProps['tone'];
  size?: BadgeProps['size'];
  /** Which edge of the pill the menu lines up with. */
  align?: 'start' | 'end';
  className?: string;
}

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * A status Badge that opens a menu of the statuses it could be, for the
 * pills on the live map's cards and lists.
 *
 *   <StatusPillMenu value={driver.status} options={DRIVER_STATUSES}
 *     onChange={(status) => update(driver.id, status)} busy={saving} />
 *
 * The pill keeps the badge's look and gains a chevron, so it reads as a
 * control. The menu is the badges themselves, flush left with no radio
 * gutter: the pill already says which one is on. Without
 * `onChange` it is the Badge it replaced, exactly, so a read-only role sees
 * no change.
 *
 * Sits inside selectable cards and rows, so a click on the pill or in its
 * menu stops before it reaches them.
 */
export const StatusPillMenu = React.forwardRef<
  HTMLButtonElement,
  StatusPillMenuProps
>(function StatusPillMenu(
  {
    value,
    options,
    onChange,
    disabled = false,
    busy = false,
    label,
    phase,
    tone,
    size,
    align = 'start',
    className,
  },
  ref
) {
  const current = options.find((option) => option.value === value);
  const words = label ?? current?.label ?? value;
  const explicit = phase !== undefined || tone !== undefined;
  const shownPhase = explicit ? phase : current?.phase;
  const shownTone = explicit ? tone : current?.tone;

  if (!onChange) {
    return (
      <Badge
        phase={shownPhase}
        tone={shownTone}
        size={size}
        className={className}
      >
        {words}
      </Badge>
    );
  }

  // One colour axis underneath, as Badge resolves it.
  const resolved = shownPhase ? (`phase-${shownPhase}` as const) : shownTone;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled || busy} onClick={stop}>
        <button
          ref={ref}
          type="button"
          title="Change status"
          className={cn(
            badgeRecipe({ tone: resolved, size }),
            statusPillTriggerRecipe(),
            className
          )}
        >
          {words}
          {busy ? (
            <Spinner size="xs" label={null} />
          ) : (
            <ChevronDown
              className={statusPillChevronRecipe()}
              aria-hidden="true"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} size="sm" onClick={stop}>
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => {
            if (next !== value) onChange(next);
          }}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              marked={false}
            >
              <Badge phase={option.phase} tone={option.tone}>
                {option.label}
              </Badge>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
